import React, { useState, useEffect } from 'react';
import { ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline';
import { apiRequest } from '../../utils/apiUtils';
import CommentComposer from './CommentComposer';
import CommentList from './CommentList';

const CommentThread = ({ issueId, currentUser }) => {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch comments on mount and when issueId changes
  useEffect(() => {
    if (issueId) {
      fetchComments();
    }
  }, [issueId]);

  const fetchComments = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiRequest(`/issues/${issueId}/comments`);

      if (response.success) {
        setComments(response.data || []);
      } else {
        setError(response.error || 'Failed to load comments');
      }
    } catch (err) {
      // Ignore throttling errors - they're just client-side rate limiting
      if (err.throttled || err.message === 'Request throttled - too frequent') {
        return;
      }
      setError(err.message || 'Failed to load comments');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateComment = async (data) => {
    try {
      const response = await apiRequest(`/issues/${issueId}/comments`, {
        method: 'POST',
        data
      });

      if (response.success) {
        // Refresh comments to get the updated list with new comment
        await fetchComments();
      } else {
        throw new Error(response.error || 'Failed to create comment');
      }
    } catch (err) {
      console.error('Error creating comment:', err);
      throw err;
    }
  };

  const handleEditComment = async (commentId, data) => {
    try {
      const response = await apiRequest(`/comments/${commentId}`, {
        method: 'PUT',
        data
      });

      if (response.success) {
        // Refresh comments to get the updated list
        await fetchComments();
      } else {
        throw new Error(response.error || 'Failed to update comment');
      }
    } catch (err) {
      console.error('Error updating comment:', err);
      throw err;
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      const response = await apiRequest(`/comments/${commentId}`, {
        method: 'DELETE'
      });

      if (response.success) {
        // Refresh comments to get the updated list
        await fetchComments();
      } else {
        throw new Error(response.error || 'Failed to delete comment');
      }
    } catch (err) {
      console.error('Error deleting comment:', err);
      alert('Failed to delete comment');
    }
  };

  const handleReplyComment = async (parentCommentId, data) => {
    try {
      const response = await apiRequest(`/issues/${issueId}/comments`, {
        method: 'POST',
        data: {
          ...data,
          parent_comment_id: parentCommentId
        }
      });

      if (response.success) {
        // Refresh comments to get the updated list with new reply
        await fetchComments();
      } else {
        throw new Error(response.error || 'Failed to create reply');
      }
    } catch (err) {
      console.error('Error creating reply:', err);
      throw err;
    }
  };

  const handleReaction = async (commentId, emoji) => {
    try {
      // Find the comment and check if user has already reacted
      const findComment = (comments) => {
        for (const comment of comments) {
          if (comment.id === commentId) return comment;
          if (comment.replies) {
            const found = findComment(comment.replies);
            if (found) return found;
          }
        }
        return null;
      };

      const comment = findComment(comments);
      const reactions = comment?.reactions || {};
      const userIds = reactions[emoji] || [];
      const hasReacted = userIds.includes(currentUser?.id);

      // Toggle reaction
      if (hasReacted) {
        // Remove reaction
        await apiRequest(`/comments/${commentId}/reactions/${emoji}`, {
          method: 'DELETE'
        });
      } else {
        // Add reaction
        await apiRequest(`/comments/${commentId}/reactions`, {
          method: 'POST',
          data: { emoji }
        });
      }

      // Refresh comments to get updated reactions
      await fetchComments();
    } catch (err) {
      console.error('Error updating reaction:', err);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-2">
        <ChatBubbleLeftRightIcon className="h-5 w-5 text-secondary-500" />
        <h3 className="text-lg font-medium text-secondary-900">
          Comments {comments.length > 0 && `(${comments.length})`}
        </h3>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative">
          {error}
        </div>
      )}

      {/* Comment Composer (Top-level) */}
      <div className="bg-white">
        <CommentComposer
          onSubmit={handleCreateComment}
          placeholder="Write a comment..."
        />
      </div>

      {/* Comments List */}
      {comments.length === 0 ? (
        <div className="text-center py-8 text-secondary-500">
          <ChatBubbleLeftRightIcon className="mx-auto h-12 w-12 text-secondary-300 mb-2" />
          <p>No comments yet. Be the first to comment!</p>
        </div>
      ) : (
        <CommentList
          comments={comments}
          currentUser={currentUser}
          onEdit={handleEditComment}
          onDelete={handleDeleteComment}
          onReply={handleReplyComment}
          onReaction={handleReaction}
        />
      )}
    </div>
  );
};

export default CommentThread;
